import { FINDER_PATTERN_SIZE } from '../domain/matrix';
import type { InnerEyeStyle, OuterEyeStyle } from '../domain/types';
import {
  circle,
  cross,
  flower,
  heart,
  leaf,
  lightning,
  polygon,
  rect,
  regularPolygon,
  roundedRect,
  shield,
  starPolygon,
  wave,
} from './geometry';

export interface EyePaths {
  /**
   * The outer ring: an outline plus a counter-wound hole, so nonzero filling
   * cuts a genuine opening. Because it is a real hole rather than an opaque
   * white square, the eyes now sit correctly on any background colour — the
   * previous renderer hardcoded `#FFFFFF` at eighteen sites and only looked
   * right on a white symbol.
   */
  readonly ring: string;
  /** The solid element at the centre of the eye. */
  readonly pupil: string;
}

/**
 * Builds one finder pattern.
 *
 * `x`/`y` are the top-left of the 7x7 block and `size` its full edge length.
 * `outerRadius`/`innerRadius` are 0..1 rounding strengths, applied by the
 * styles that support them — previously these were accepted as props,
 * threaded through the whole tree, and then never read by anything.
 */
export function eyePaths(
  outerStyle: OuterEyeStyle,
  innerStyle: InnerEyeStyle,
  x: number,
  y: number,
  size: number,
  outerRadius: number,
  innerRadius: number,
): EyePaths {
  const unit = size / FINDER_PATTERN_SIZE;
  const cx = x + size / 2;
  const cy = y + size / 2;

  return {
    ring: outerRingPath(outerStyle, x, y, size, unit, cx, cy, outerRadius),
    pupil: pupilPath(innerStyle, x, y, unit, cx, cy, innerRadius),
  };
}

/** The square hole every ring shares: the 5x5 light area inside the border. */
function ringHole(x: number, y: number, unit: number): string {
  return rect(x + unit, y + unit, unit * 5, unit * 5, true);
}

function outerRingPath(
  style: OuterEyeStyle,
  x: number,
  y: number,
  size: number,
  unit: number,
  cx: number,
  cy: number,
  radiusRatio: number,
): string {
  const hole = ringHole(x, y, unit);

  switch (style) {
    case 'square':
      return rect(x, y, size, size) + hole;

    case 'rounded':
      // Full strength rounds the square all the way to a circle.
      return roundedRect(x, y, size, size, (size / 2) * radiusRatio) + hole;

    case 'circle':
      return circle(cx, cy, size / 2) + circle(cx, cy, size / 2 - unit, true);

    case 'rounded_top_left':
      return roundedRect(x, y, size, size, unit * 2 * radiusRatio, { tl: true }) + hole;

    case 'rounded_top_right':
      return roundedRect(x, y, size, size, unit * 2 * radiusRatio, { tr: true }) + hole;

    case 'rounded_bottom_left':
      return roundedRect(x, y, size, size, unit * 2 * radiusRatio, { bl: true }) + hole;

    case 'rounded_bottom_right':
      return roundedRect(x, y, size, size, unit * 2 * radiusRatio, { br: true }) + hole;

    case 'diagonal':
      return (
        polygon([
          [x, y],
          [x + size * 0.7, y],
          [x + size, y + size * 0.3],
          [x + size, y + size],
          [x + size * 0.3, y + size],
          [x, y + size * 0.7],
        ]) + hole
      );

    case 'dots': {
      // A ring of discs tracing the 7x7 border, no hole needed.
      const r = unit * 0.45;
      let d = '';
      for (let i = 0; i < FINDER_PATTERN_SIZE; i++) {
        d += circle(x + (i + 0.5) * unit, y + 0.5 * unit, r);
        d += circle(x + (i + 0.5) * unit, y + 6.5 * unit, r);
      }
      for (let i = 1; i < FINDER_PATTERN_SIZE - 1; i++) {
        d += circle(x + 0.5 * unit, y + (i + 0.5) * unit, r);
        d += circle(x + 6.5 * unit, y + (i + 0.5) * unit, r);
      }
      return d;
    }

    case 'diamond':
      return (
        polygon([
          [cx, y],
          [x + size, cy],
          [cx, y + size],
          [x, cy],
        ]) +
        polygon(
          [
            [cx, y + unit],
            [x + size - unit, cy],
            [cx, y + size - unit],
            [x + unit, cy],
          ],
          true,
        )
      );

    case 'flower_shape':
      return flower(cx, cy, size / 2, 8) + hole;

    case 'star_shape':
      return starPolygon(cx, cy, size / 2, size / 4, 5) + hole;

    case 'hexagon':
      return (
        regularPolygon(cx, cy, size / 2, 6) + regularPolygon(cx, cy, size / 2 - unit, 6, 0, true)
      );

    case 'shield':
      return shield(cx, cy, size * 0.94, size * 0.98) + hole;

    case 'wave':
      return wave(cx, cy, size * 0.42, 6, size * 0.07) + hole;

    case 'gear':
      return starPolygon(cx, cy, size / 2, size / 3, 8) + hole;
  }
}

function pupilPath(
  style: InnerEyeStyle,
  x: number,
  y: number,
  unit: number,
  cx: number,
  cy: number,
  radiusRatio: number,
): string {
  // The classic pupil is the middle 3x3 of the 7x7 block.
  const px = x + unit * 2;
  const py = y + unit * 2;
  const pSize = unit * 3;

  switch (style) {
    case 'square':
      return rect(px, py, pSize, pSize);

    case 'rounded':
      return roundedRect(px, py, pSize, pSize, (pSize / 2) * radiusRatio);

    case 'circle':
      return circle(cx, cy, pSize / 2);

    case 'dots_3x3': {
      let d = '';
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          d += circle(cx + i * unit, cy + j * unit, unit * 0.35);
        }
      }
      return d;
    }

    case 'diamond':
      return polygon([
        [cx, py],
        [px + pSize, cy],
        [cx, py + pSize],
        [px, cy],
      ]);

    case 'leaf':
      return leaf(cx, cy, pSize);

    case 'vertical_bars': {
      const barWidth = unit * 0.6;
      let d = '';
      for (let i = 0; i < 3; i++) {
        d += rect(px + i * unit + (unit - barWidth) / 2, py, barWidth, pSize);
      }
      return d;
    }

    case 'horizontal_bars': {
      const barHeight = unit * 0.6;
      let d = '';
      for (let i = 0; i < 3; i++) {
        d += rect(px, py + i * unit + (unit - barHeight) / 2, pSize, barHeight);
      }
      return d;
    }

    case 'star':
      return starPolygon(cx, cy, pSize / 2, pSize / 4, 5);

    case 'heart':
      return heart(cx, cy, pSize);

    case 'flower':
      return flower(cx, cy, pSize / 2, 6);

    case 'cross':
      return cross(cx, cy, pSize, unit * 0.9);

    case 'triangle':
      return polygon([
        [cx, py],
        [px + pSize, py + pSize],
        [px, py + pSize],
      ]);

    case 'hexagon':
      return regularPolygon(cx, cy, pSize / 2, 6);

    case 'wave':
      return wave(cx, cy, pSize * 0.42, 4, pSize * 0.1);

    case 'lightning':
      return lightning(cx, cy, pSize);

    case 'target':
      // Disc, counter-wound hole, then a solid centre dot.
      return (
        circle(cx, cy, pSize / 2) + circle(cx, cy, pSize / 3, true) + circle(cx, cy, pSize / 6)
      );
  }
}
